/**
 * Shared badges and chips for the Member Activation builder (AR-328).
 *
 * Kept in one place so a step type, a requirement or a route reads identically
 * in the module list, the editor panels, the archive and the KPI views.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import {
  ACTIVATION_ROUTES,
  REQUIREMENT_BADGE_CLASSES,
  REQUIREMENT_LABELS,
  STEP_TYPE_STYLES,
  type ActivationRequirement,
  type ActivationRouteCode,
  type ActivationStepType,
} from '@/lib/activationBuilder';

export const TypeBadge: React.FC<{ type: ActivationStepType; className?: string }> = ({
  type,
  className,
}) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[10px] text-gray-600',
      'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
      className,
    )}
  >
    <span className={cn('h-1.5 w-1.5 rounded-full', STEP_TYPE_STYLES[type].dot)} aria-hidden />
    {type}
  </span>
);

export const RequirementBadge: React.FC<{ requirement: ActivationRequirement }> = ({
  requirement,
}) => (
  <span
    className={cn(
      'shrink-0 rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider',
      REQUIREMENT_BADGE_CLASSES[requirement],
    )}
  >
    {REQUIREMENT_LABELS[requirement]}
  </span>
);

/**
 * The four route chips, always rendered in the same order so the columns line
 * up down the list. Routes the item is hidden from render muted.
 */
export const RouteChips: React.FC<{
  routes: ActivationRouteCode[];
  /** Omit the muted chips entirely (used on step rows). */
  activeOnly?: boolean;
}> = ({ routes, activeOnly = false }) => (
  <span className="flex shrink-0 items-center gap-1">
    {ACTIVATION_ROUTES.filter((route) => !activeOnly || routes.includes(route.code)).map(
      (route) => {
        const on = routes.includes(route.code);
        return (
          <span
            key={route.code}
            title={`${route.label} — ${on ? 'visible' : 'hidden'}`}
            className={cn(
              'rounded border px-1.5 py-0.5 text-[9px] font-semibold',
              on
                ? 'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                : 'border-gray-100 bg-gray-50 text-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-600',
            )}
          >
            {route.short}
          </span>
        );
      },
    )}
  </span>
);

/** Eyebrow label that opens each block of the editor panels. */
export const PanelSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
    {children}
  </p>
);

export const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">{children}</p>
);

/** Read-only text box used for prompts, system fields and system logic. */
export const ReadOnlyBox: React.FC<{ value: string; mono?: boolean }> = ({ value, mono }) => (
  <div
    className={cn(
      'whitespace-pre-line rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-700',
      'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300',
      mono && 'font-mono',
    )}
  >
    {value}
  </div>
);

export const VariablePill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-md border border-teal-200 bg-teal-50 px-2 py-1 font-mono text-[10.5px] font-semibold text-teal-700 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300">
    {children}
  </span>
);
